import { NgClass } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { AfterViewInit, booleanAttribute, Component, computed, DestroyRef, effect, ElementRef, inject, input, OnDestroy, OnInit, output, Signal, untracked, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, first, startWith } from 'rxjs';

import { AppMaterialModule } from '../../../material.module';
import { AttributeDirective } from 'src/app/infrastructure/directives/attribute.directive';
import { InputType } from '../../models/input-type';
import { _MatFormFieldComponent } from '../../components/_mat-form-field.component';

@Component({
    selector: 'app-input-control',
    templateUrl: './input-control.component.html',
    styleUrls: [
        './input-control.component.scss',
        '../../control.scss',
    ],
    standalone: true,
    host: {
        '[class.readonly]': 'readonly$$()'
    },
    imports: [
        AppMaterialModule,
        AttributeDirective,
        NgClass,
        ReactiveFormsModule,
    ],
})
export class InputControlComponent
    extends _MatFormFieldComponent
    implements AfterViewInit, OnInit, OnDestroy
{
    private readonly __destroyRef = inject(DestroyRef);

    override attributes$$        = computed(() => ({
        ...this._input_attributes$$(),
        'autocomplete': this.input_htmlAutocomplete$$(),
        'type':         this.input_type$$(),
    }));
    override autofocus$$         = this._input_autofocus$$;
    override control$$           = this._control$$;
    override controlName$$       = this._input_controlName$$;
    override disabled$$          = this._input_disabled$$;
    override errorShow$$         = this._input_errorShow$$;
    override errorStateMatcher$$ = this._input_errorStateMatcher$$;
    override id$$                = this._input_id$$;
    override lockOnLoad$$        = this._input_lockOnLoad$$;
    override placeholder$$       = this._input_placeholder$$;
    override readonly$$          = this._input_readonly$$;
    override subscriptHide$$     = this._input_subscriptHide$$;
    override tabIndex$$          = this._tabIndex$$;
    override value$$             = this._input_value$$;
    override valueReset$$        = this._input_valueReset$$;

    // mat-form-field:
    override appearance$$         = this._input_appearance$$;
    override classHeight$$        = this._input_classHeight$$;
    override color$$              = this._input_color$$;
    override floatLabel$$         = this._input_floatLabel$$;
    override hideLineRipple$$     = this._input_hideLineRipple$$;
    override hideRequiredMarker$$ = this._hideRequiredMarker$$;
    override hint$$               = this._input_hint$$;
    override hintAlign$$          = this._input_hintAlign$$;
    override hintLabel$$          = this._input_hintLabel$$;
    override label$$              = this._input_label$$;
    // Signal (geen InputSignal): DropdownControl leidt 'required' mede af uit de validators.
    override required$$: Signal<boolean> = this._input_required$$;
    override subscriptSizing$$    = this._input_subscriptSizing$$;

    // mat-input:
    input_disabledInteractive$$  = input(false,                         { transform: booleanAttribute, alias: 'disabledInteractive' });
    input_type$$                 = input<InputType>('text',             { alias: 'type' });

    // input:
    input_autocomplete$$         = input<any | undefined>(undefined,    { alias: 'autocomplete' });
    input_htmlAutocomplete$$     = input<string>('off',                 { alias: 'htmlAutocomplete' });
    input_keyUpReset$$           = input<boolean>(false,                { alias: 'keyUpReset' });
    input_validateOnSubmit$$     = input<boolean>(false,                { alias: 'validateOnSubmit' });

    // Knop rechts in het veld (zoals de datepicker-toggle), bv. om een keuzedialoog te openen. Zonder icoon geen knop.
    input_suffixIcon$$           = input<string | null>(null,           { alias: 'suffixIcon' });
    input_suffixTooltip$$        = input<string | null>(null,           { alias: 'suffixTooltip' });
    out_suffixClick              = output<void>({ alias: 'suffixClick' });

    @ViewChild('inputField', { read: ElementRef }) inputField?: ElementRef<HTMLInputElement> | null;

    // Volgt [value] live i.p.v. eenmalig in ngOnInit: de waarde kan na het tonen nog binnenkomen
    // (bv. de omschrijving uit de foto-metadata in ImageDetails). Een lege beginwaarde laat de
    // control met rust; is er eenmaal een waarde gezet, dan wordt ook leegmaken doorgegeven.
    private __valueApplied = false;
    private __value_sync = effect(() => {
        const __value = this._input_value$$();
        const __control = this.control$$();
        if (!__value && !this.__valueApplied) return;
        untracked(() => __control.setValue(__value));
        this.__valueApplied = true;
    });

    override ngOnInit(): void {
        super._ngOnInit();
    }

    // Het HTML-autofocus-attribuut werkt maar één keer per document (bij een later getoond veld,
    // bv. de 2FA-code na login, dus niet) en niet op een disabled veld (lockOnLoad tijdens het
    // laden). Daarom zelf focussen, zodra de control enabled is.
    ngAfterViewInit(): void {
        if (!this.autofocus$$()) return;

        const __control = this.control$$();
        __control.statusChanges.pipe(
            startWith(__control.status),
            filter(status => status !== 'DISABLED'),
            first(),
            takeUntilDestroyed(this.__destroyRef),
        ).subscribe(() => setTimeout(() => this.inputField?.nativeElement.focus()));
    }

    emit_suffixClick(event: MouseEvent): void {
        event.stopPropagation();
        this.out_suffixClick.emit();
    }

    override errorMessage_getFirst(): string {
        return this._validationMessage(this.control$$()?.errors);
    }

    override ngOnDestroy(): void {
        super._ngOnDestroy();
    }
}
